import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import clsx from 'clsx';

import { Avatar } from 'shared/components/ui/Avatar/Avatar';
import { ROUTES, coinIcons } from 'shared/constants';
import { DISCORD_USERNAME_LENGTH } from 'shared/constants/constants';
import { RoomStatusEnum } from 'shared/constants/shared-enums';
import { useCurrenciesStore } from 'shared/store/currenciesStore';
import { useGlobalStore } from 'shared/store/globalStore';
import { RoomInfo } from 'shared/types/app-types';
import { genericFormatBet } from 'shared/utils/betFormater';
import { prettyTx } from 'shared/utils/pretty-tx';

import humanLeft from 'assets/png/explor-human-left.png';
import humanRight from 'assets/png/explor-human-right.png';
import floor from 'assets/png/floor.png';

/** Props for subcomponent that renders a single player's info. */
interface IPlayerInfoProps {
  playerAddress: `0x${string}`;
  wins: number;
  align: 'left' | 'right';
}

/**
 * Renders a player's avatar, win count, and shortened address.
 */
const PlayerInfo: FC<IPlayerInfoProps> = ({ playerAddress, wins, align }) => {
  const containerClasses = clsx(
    'mb-1 box-border flex items-center lg:mb-2',
    'border border-[rgba(255,255,255,0.25)] bg-[rgba(13,20,80,0.25)] lg:border-2',
    'h-8 w-[58px] backdrop-blur-2xl lg:h-16 lg:w-[116px]',
    'relative rounded-lg lg:rounded-2xl',
  );

  // Position the avatar on the left or right based on `align`.
  const avatarClasses = clsx(
    'h-8 w-8 lg:h-16 lg:w-16',
    'border border-orange-btn lg:border-2',
    'absolute rounded-lg lg:rounded-2xl',
    {
      'left-[-2px] top-[-2px]': align === 'left',
      'right-[-2px] top-[-2px]': align === 'right',
    },
  );

  // The numeric win label is offset depending on alignment.
  const winsClasses = clsx(
    'text-center text-base lg:text-[32px]',
    'flex w-[26px] items-center justify-center lg:w-[52px]',
    {
      'ml-[30px] lg:ml-[60px]': align === 'left',
    },
  );

  const leaderboardMap = useGlobalStore((state) => state.leaderboardMap);
  const screenAddress = useMemo(
    () =>
      prettyTx({
        address: playerAddress,
        leaderboardMap,
        discordUsernameLength: DISCORD_USERNAME_LENGTH,
      }),
    [playerAddress, leaderboardMap],
  );
  return (
    <div className="flex flex-col items-center">
      <div className={containerClasses}>
        {align === 'left' && (
          <>
            <Avatar
              userAddress={playerAddress.toLowerCase() as `0x${string}`}
              className={avatarClasses}
            />
            <span className={winsClasses}>{wins}</span>
          </>
        )}
        {align === 'right' && (
          <>
            <span className={winsClasses}>{wins}</span>
            <Avatar
              userAddress={playerAddress.toLowerCase() as `0x${string}`}
              className={avatarClasses}
            />
          </>
        )}
      </div>
      <div className="rounded-[7px] border border-border bg-dark-background p-1 text-[8px] leading-none lg:p-2 lg:text-base">
        <span className="font-chivo">{screenAddress}</span>
      </div>
    </div>
  );
};

interface IExploreCardProps {
  room: RoomInfo;
  chainId?: number;
}

export const ExploreCard: FC<IExploreCardProps> = ({ room, chainId }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const availableCurrencies = useCurrenciesStore((state) => state.availableCurrencies);

  const { data: roomData, winsCounter, roundCounter, status } = room;

  // Find currency
  const currencyObject = useMemo(
    () => availableCurrencies.find((c) => c.address.toLowerCase() === roomData.token.toLowerCase()),
    [availableCurrencies, roomData.token],
  );

  const formattedBet = genericFormatBet(
    ((Number(roomData.bet) * 2) / 10 ** currencyObject?.decimals).toString(),
    'text-sm',
  );

  return (
    <div
      onClick={() => navigate(`${ROUTES.STREAM}/${chainId}/${Number(roomData.roomId)}`)}
      className="relative flex h-[9.625rem] w-full cursor-pointer flex-col items-center overflow-hidden rounded-2xl border border-border bg-explore-gradient p-4 text-white lg:h-[19.375rem] lg:w-[23rem] lg:rounded-[2rem] lg:border-2 lg:p-8"
      data-testid="explore-card"
    >
      {/* Background images */}
      <img
        src={floor}
        alt="floor"
        className="absolute left-[calc(50%-1000px/2)] top-[calc(50%-99px/2+46px)] z-0 h-[200px] min-w-[1000px] lg:top-[calc(50%-199px/2+92px)]"
      />
      <img
        src={humanLeft}
        alt="human-left"
        className="z-1 w-4.4rem absolute bottom-0 left-0 top-0 h-full lg:w-auto"
      />
      <img
        src={humanRight}
        alt="human-right"
        className="z-1 w-4.4rem absolute bottom-0 right-0 top-0 h-full lg:w-auto"
      />

      {/* Bet info */}
      <div className="mb-3 flex items-center text-2xl leading-none lg:mb-8 lg:text-4xl">
        <span className="mr-2 font-black">{formattedBet}</span>
        <div className="mr-2 h-1 w-1 rounded-full bg-white opacity-50 lg:h-2 lg:w-2" />
        {currencyObject && (
          <img
            src={coinIcons?.[currencyObject?.symbol as keyof typeof coinIcons]}
            className="mr-1 h-[18px] w-[18px] lg:h-9 lg:w-9"
          />
        )}
        <span className="font-bold">{currencyObject?.symbol}</span>
      </div>

      {/* Players */}
      <div className="z-50 flex items-center gap-1 lg:gap-2">
        <PlayerInfo
          playerAddress={roomData.playerA}
          wins={Number(winsCounter.winsPlayerA)}
          align="left"
        />
        <span className="text-[24px] font-bold lg:text-[48px]">{t('common.vs')}</span>
        <PlayerInfo
          playerAddress={roomData.playerB}
          wins={Number(winsCounter.winsPlayerB)}
          align="right"
        />
      </div>

      {/* Footer */}
      <div className="absolute bottom-0 left-0 right-0 flex items-center justify-between bg-explore-footer-gradient-grape p-2.5 lg:p-5">
        <div className="flex items-center gap-1 text-[14px] font-bold leading-none lg:gap-2 lg:text-[28px]">
          {status !== RoomStatusEnum.Closed && (
            <>
              <div className="h-2 w-2 animate-pulse rounded-full bg-[#0AF33D] lg:h-[10px] lg:w-[10px]" />
              <p>{t('common.live')}</p>
              <div className="h-0.5 w-0.5 rounded-full bg-white opacity-50 lg:h-1 lg:w-1" />
            </>
          )}
          <p>
            {t('common.round')} {roundCounter}
          </p>
        </div>
        <div className="flex items-center gap-1 text-sm font-medium leading-none lg:gap-2 lg:text-[20px]">
          <span>{t('pages.liveGamesSection.id')}</span>
          <span>{Number(roomData.roomId)}</span>
        </div>
      </div>
    </div>
  );
};
